import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutGrid, Inbox, Users, SlidersHorizontal, Activity, Film, LogOut, Baby, UserCog } from "lucide-react";
import { Logo } from "../components/Brand";
import { Overview } from "../components/parent/Overview";
import { ApprovalQueue } from "../components/parent/ApprovalQueue";
import { KidManager } from "../components/parent/KidManager";
import { KidControls } from "../components/parent/KidControls";
import { ActivityLog } from "../components/parent/ActivityLog";
import { VideoLibrary } from "../components/parent/VideoLibrary";
import { NotificationBell } from "../components/parent/Notifications";
import { AccountPanel } from "../components/parent/AccountPanel";
import { MobileTabs } from "../components/parent/MobileTabs";
import { InstallPrompt } from "../components/parent/InstallPrompt";
import { VerifyBanner } from "../components/parent/VerifyBanner";
import { parentApi } from "../lib/api";
import { registerParentPush, unregisterParentPush } from "../lib/native";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { id: "overview", label: "Overview", short: "Home", icon: LayoutGrid },
  { id: "approvals", label: "Approvals", short: "Review", icon: Inbox },
  { id: "kids", label: "Kid profiles", short: "Kids", icon: Users },
  { id: "controls", label: "Controls", icon: SlidersHorizontal },
  { id: "videos", label: "All videos", icon: Film },
  { id: "activity", label: "Activity", icon: Activity },
  { id: "account", label: "Account", icon: UserCog },
];

export default function ParentDashboard() {
  const { parent, logout, logoutKid } = useAuth();
  useEffect(() => { logoutKid(); registerParentPush(); }, [logoutKid]);
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [kids, setKids] = useState([]);
  const [stats, setStats] = useState(null);

  const refresh = useCallback(() => {
    parentApi.get("/kids").then((r) => setKids(r.data));
    parentApi.get("/parent/overview").then((r) => setStats(r.data));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const pending = (stats?.pending_videos || 0) + (stats?.pending_comments || 0);
  const panels = {
    overview: <Overview stats={stats} goTo={setTab} />,
    approvals: <ApprovalQueue onChange={refresh} />,
    kids: <KidManager kids={kids} onChange={refresh} />,
    controls: <KidControls kids={kids} onChange={refresh} />,
    videos: <VideoLibrary onChange={refresh} />,
    activity: <ActivityLog kids={kids} />,
    account: <AccountPanel />,
  };

  return (
    <div className="min-h-screen bg-[#F4F1FF]">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b-[3px] border-[#1B1A2E] pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 sm:h-20 flex items-center gap-2 sm:gap-4">
          <Logo className="text-xl sm:text-2xl" />
          <span className="zn-chip bg-[#7B5CFA] text-white !py-1 text-xs hidden sm:inline-flex">Parent zone</span>
          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <span className="hidden md:block text-sm">Hi, <b>{parent?.name}</b></span>
            <NotificationBell />
            <button data-testid="parent-kid-mode-btn" onClick={() => nav("/profiles")} className="zn-btn bg-[#FFD23F] !py-2 !px-3 sm:!px-4 text-sm sm:text-base" title="Kid mode"><Baby size={16} /> <span className="hidden min-[400px]:inline">Kid mode</span></button>
            <button data-testid="parent-logout-btn" onClick={async () => { await unregisterParentPush(); logout(); nav("/"); }} className="zn-btn bg-white !py-2 !px-3" title="Log out"><LogOut size={16} /></button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-8 pb-[calc(6.5rem+env(safe-area-inset-bottom))] lg:pb-8 grid grid-cols-1 lg:grid-cols-[230px_1fr] gap-6 lg:gap-8">
        <nav className="hidden lg:flex lg:flex-col gap-2">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} data-testid={`parent-tab-${id}`} onClick={() => setTab(id)}
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl border-[3px] font-display font-medium whitespace-nowrap transition-colors ${tab === id ? "bg-[#1B1A2E] text-white border-[#1B1A2E]" : "bg-white border-transparent hover:border-[#1B1A2E]"}`}>
              <Icon size={18} /> {label}
              {id === "approvals" && pending > 0 && <span data-testid="approvals-badge" className="ml-auto bg-[#FF6B4A] text-white text-xs rounded-full px-2 py-0.5">{pending}</span>}
            </button>
          ))}
        </nav>
        <section key={tab} className="zn-pop min-w-0"><VerifyBanner />{panels[tab]}</section>
      </div>
      <MobileTabs tabs={TABS} tab={tab} setTab={setTab} pending={pending} />
      <InstallPrompt />
    </div>
  );
}
